import React, { useState, useRef, useEffect } from 'react';
import { Mail, Crown, Unlink, Plus, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ConfirmationModal } from '../../ui/ConfirmationModal';
import { useCampaignContext } from '../../../contexts/CampaignContext';
import { useToast } from '../../../hooks/useToast';
import { apiClient } from '../../../lib/api';

interface GmailConnection {
  id: string;
  is_connected: boolean;
  gmail_email: string;
  display_name: string;
  is_primary: boolean;
  needs_refresh: boolean;
  token_status: string;
  created_at: string;
}

const EmailSectionContent: React.FC = () => {
  const {
    gmailConnections: connections,
    isLoadingConnections: isLoading,
    refreshConnections,
  } = useCampaignContext();
  const { showToast } = useToast();

  const [isConnecting, setIsConnecting] = useState(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [updatingPrimaryId, setUpdatingPrimaryId] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<GmailConnection | null>(null);
  const [confirmMakePrimary, setConfirmMakePrimary] = useState<GmailConnection | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
      }
    };
  }, []);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const response = await apiClient.getGmailAuthUrl();

      const popup = window.open(
        response.auth_url,
        'gmail-oauth',
        'width=500,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
      );

      if (!popup) {
        setIsConnecting(false);
        return;
      }

      const cleanup = () => {
        if (messageHandlerRef.current) {
          window.removeEventListener('message', messageHandlerRef.current);
          messageHandlerRef.current = null;
        }
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GMAIL_OAUTH_SUCCESS') {
          setIsConnecting(false);
          if (event.data.warning === 'read_scope_missing') {
            // SKY-469: send works but read was declined — sync is off
            showToast(
              'Gmail connected without read access — email sync is off. Reconnect and allow "View your email messages".',
              'error'
            );
          }
          refreshConnections();
          cleanup();
        } else if (event.data.type === 'GMAIL_OAUTH_ERROR') {
          setIsConnecting(false);
          cleanup();
        }
      };

      messageHandlerRef.current = handleMessage;
      window.addEventListener('message', handleMessage);
      timeoutIdRef.current = setTimeout(() => {
        cleanup();
        setIsConnecting(false);
      }, 300000);
    } catch {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirmDisconnect) return;

    try {
      setDisconnectingId(confirmDisconnect.id);
      const isPrimaryBeingDisconnected = confirmDisconnect.is_primary;

      await apiClient.disconnectGmail(confirmDisconnect.id);

      if (isPrimaryBeingDisconnected) {
        const remainingConnections = connections.filter(conn => conn.id !== confirmDisconnect.id);
        if (remainingConnections.length > 0) {
          try {
            await apiClient.setPrimaryGmailConnection(remainingConnections[0].id);
          } catch {
            // Silent fail for auto-primary
          }
        }
      }

      refreshConnections();
    } catch {
      // Error handled silently
    } finally {
      setDisconnectingId(null);
      setConfirmDisconnect(null);
    }
  };

  const handleSetPrimary = async () => {
    if (!confirmMakePrimary) return;

    try {
      setUpdatingPrimaryId(confirmMakePrimary.id);
      await apiClient.setPrimaryGmailConnection(confirmMakePrimary.id);
      refreshConnections();
    } catch {
      // Error handled silently
    } finally {
      setUpdatingPrimaryId(null);
      setConfirmMakePrimary(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5">
        <div>
          <h3 className="text-[13px] font-semibold text-neutral-900">Email Accounts</h3>
          <p className="text-[11px] text-neutral-500">
            Connect Gmail accounts to send emails
          </p>
        </div>
      </div>

      {connections.length === 0 ? (
        <div className="border-2 border-dashed border-neutral-200 rounded-xl p-8 text-center bg-neutral-50/50">
          <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mx-auto mb-3">
            <Mail className="w-6 h-6 text-primary-600" />
          </div>
          <h4 className="text-[14px] font-semibold text-neutral-900 mb-1">No Gmail Connected</h4>
          <p className="text-[12px] text-neutral-500 mb-4">
            Connect Gmail to send emails
          </p>
          <Button
            id="connect-gmail-empty"
            variant="primary"
            size="sm"
            onClick={handleConnect}
            disabled={isConnecting}
            loading={isConnecting}
            leftIcon={!isConnecting ? <ExternalLink className="w-4 h-4" /> : undefined}
            aria-label="Connect Gmail account"
          >
            {isConnecting ? 'Connecting...' : 'Connect Gmail Account'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map((connection) => {
            const isExpired = connection.token_status === 'expired';
            const isPrimary = connection.is_primary;

            return (
              <div
                key={connection.id}
                className={`group relative flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${
                  isExpired
                    ? 'bg-error-50/80 border border-error-100'
                    : 'bg-neutral-50/80 border border-neutral-200'
                }`}
              >
                <div className="relative flex-shrink-0 hidden sm:block">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isExpired
                        ? 'bg-error-100'
                        : isPrimary
                          ? 'bg-neutral-900'
                          : 'bg-neutral-100'
                    }`}
                  >
                    <Mail
                      className={`w-4 h-4 ${
                        isExpired
                          ? 'text-error-500'
                          : isPrimary
                            ? 'text-white'
                            : 'text-neutral-500'
                      }`}
                    />
                  </div>
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                      isExpired ? 'bg-error-500' : 'bg-success-500'
                    }`}
                  />
                </div>

                <div
                  className={`sm:hidden w-2 h-2 rounded-full flex-shrink-0 ${
                    isExpired ? 'bg-error-500' : 'bg-success-500'
                  }`}
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-semibold text-neutral-900 truncate">
                      {connection.gmail_email}
                    </span>
                    {isPrimary && !isExpired && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded-full uppercase tracking-wide bg-neutral-900 text-white flex-shrink-0">
                        <Crown className="w-2.5 h-2.5" />
                        Primary
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[11px] font-medium block truncate ${
                      isExpired ? 'text-error-500' : 'text-success-600'
                    }`}
                  >
                    {isExpired ? 'Needs reconnection' : 'Connected & active'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isExpired ? (
                    <Button
                      id={`reconnect-gmail-${connection.id}`}
                      variant="primary"
                      size="sm"
                      onClick={handleConnect}
                      disabled={isConnecting}
                      loading={isConnecting}
                      leftIcon={!isConnecting ? <ExternalLink className="w-3 h-3" /> : undefined}
                      aria-label={`Reconnect ${connection.gmail_email}`}
                      className="text-[11px] px-2 py-1"
                    >
                      Reconnect
                    </Button>
                  ) : (
                    <>
                      {!isPrimary && (
                        <Button
                          id={`make-primary-${connection.id}`}
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmMakePrimary(connection)}
                          disabled={updatingPrimaryId === connection.id}
                          loading={updatingPrimaryId === connection.id}
                          leftIcon={updatingPrimaryId !== connection.id ? <Crown className="w-3 h-3" /> : undefined}
                          aria-label={`Make ${connection.gmail_email} primary`}
                          className="text-[11px] px-2 py-1"
                        >
                          Make Primary
                        </Button>
                      )}
                      <Button
                        id={`disconnect-gmail-${connection.id}`}
                        variant="secondary"
                        size="sm"
                        onClick={() => setConfirmDisconnect(connection)}
                        disabled={disconnectingId === connection.id}
                        loading={disconnectingId === connection.id}
                        leftIcon={disconnectingId !== connection.id ? <Unlink className="w-3 h-3" /> : undefined}
                        aria-label={`Disconnect ${connection.gmail_email}`}
                        className="text-[11px] px-2 py-1"
                      >
                        Disconnect
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <Button
            id="add-another-gmail"
            variant="secondary"
            fullWidth
            onClick={handleConnect}
            disabled={isConnecting}
            loading={isConnecting}
            leftIcon={!isConnecting ? <Plus className="w-4 h-4" /> : undefined}
            aria-label="Add another Gmail account"
            className="border-dashed"
          >
            {isConnecting ? 'Connecting...' : 'Add Another Gmail Account'}
          </Button>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!confirmDisconnect}
        onClose={() => setConfirmDisconnect(null)}
        onConfirm={handleDisconnect}
        title="Disconnect Gmail Account"
        message={`Are you sure you want to disconnect ${confirmDisconnect?.gmail_email}? You will need to reconnect to send emails from this account.`}
        confirmText="Disconnect"
        variant="danger"
        isLoading={!!disconnectingId}
      />

      <ConfirmationModal
        isOpen={!!confirmMakePrimary}
        onClose={() => setConfirmMakePrimary(null)}
        onConfirm={handleSetPrimary}
        title="Make Primary Account"
        message={`Set ${confirmMakePrimary?.gmail_email} as your primary email account? This will be used as the default sender for emails.`}
        confirmText="Make Primary"
        variant="default"
        isLoading={!!updatingPrimaryId}
      />
    </div>
  );
};

export const EmailSection: React.FC = () => {
  return <EmailSectionContent />;
};
